/**
 * Vibration feedback, on the same events as the notes.
 *
 * Every call is doubly guarded. `navigator.vibrate` is simply absent on iOS
 * Safari — no polyfill, no permission prompt, no plan to add one — so the
 * feature test is the normal path there, not an error case. The try/catch
 * covers browsers that expose the method but throw when it is called outside
 * a user gesture or with the page hidden.
 *
 * Not tied to the mute toggle, deliberately: muting is what you do when you
 * need the room to stay quiet, and that is exactly when a silent buzz is the
 * feedback you still want. Phones make the same distinction.
 */

type Vibrator = { vibrate?: (pattern: number | number[]) => boolean };

function buzz(pattern: number | number[]): void {
  try {
    const nav = navigator as Navigator & Vibrator;
    if (typeof nav.vibrate === "function") nav.vibrate(pattern);
  } catch {
    /* unsupported or blocked — haptics are never load-bearing */
  }
}

/** Entering a new tile. Deliberately at the threshold of perception. */
export function tapForward(): void {
  buzz(8);
}

/** Backing out of a tile — lighter than forward, so undo feels different. */
export function tapBackward(): void {
  buzz(4);
}

/** Clearing the board. */
export function tapReset(): void {
  buzz(14);
}

/** Solved: a short celebratory pattern (vibrate, pause, vibrate...). */
export function tapWin(): void {
  buzz([14, 60, 14, 60, 36]);
}
